// External Imports
import React, { useRef } from "react";

// Internal Imports
import { Modal } from "../../../../components/core/modal/Modal";
import {
    TermsModalContainer,
    TermsModalContentContainer,
    TermsModalParagraph,
    TermsModalTitle,
    TermsModalSubtitle,
    TermsModalLink,
    TermsModalBoldText,
    TermsModalUnorderList,
    TermsModalList,
    TermsModalBtns,
    PrintableArea,
} from "./TermsModal.styles";
import WebsiteButton from "../../../../components/core/button/WebsiteButton";
import PrintButton from "../../../../components/core/button/print/PrintButton";
// Language Context
import { useLanguage } from "../../../../core/translations/LanguageContext";

// Translations
import { TermsAndConditionsContentModal } from "./translations/types";
import { enTranslations } from "./translations/en";
import { deTranslations } from "./translations/de";
import { SupportedLanguage } from "../../../../core/translations/supportedLanguages";

const TRANSLATIONS: Record<SupportedLanguage, TermsAndConditionsContentModal> =
    {
        en: enTranslations,
        de: deTranslations,
    };
/**
 * Props for the TermsModalPreview component
 */
interface TermsModalProps {
    onRequestClose: () => void;
}

/**
 * A modal that displays a terms and conditions. This contains a terms and conditions for the game, with
 * a single button to close the modal.
 *
 * @param onClose - A function to call when the modal is closed
 */
export const TermsModalPreview: React.FC<TermsModalProps> = ({
    onRequestClose,
}) => {
    const { language } = useLanguage();
    const translations = TRANSLATIONS[language];

    /**
     * Include useRef at the top of the page and create const from it to include on page section.
     * After adding this to printable section pass the same in PrintButton component
     * Then print button will print the section provided to it.
     */

    const componentRef = useRef();
    // Logic to close the modal
    const closeModal = () => {
        onRequestClose();
    };


    return (
        <Modal onRequestClose={closeModal}>
            <TermsModalContainer>
                <TermsModalContentContainer>
                    {/* @ts-ignore */}
                    <PrintableArea ref={componentRef}>
                        <TermsModalTitle>{translations.title}</TermsModalTitle>
                        <TermsModalParagraph>
                            {translations.content.start}{" "}
                            <TermsModalLink
                                href={translations.content.websiteLink?.href}
                            >
                                {translations.content.websiteLink?.text}
                            </TermsModalLink>{" "}
                            {translations.content.end}
                        </TermsModalParagraph>
                        <TermsModalParagraph>
                            <TermsModalBoldText>
                                {translations.boldContent}
                            </TermsModalBoldText>
                        </TermsModalParagraph>
                        {translations.sections.map((section) => (
                            <>
                                <TermsModalSubtitle>
                                    {section.title}
                                </TermsModalSubtitle>
                                {section.content?.map((paragraph) => (
                                    <TermsModalParagraph>
                                        {paragraph}
                                    </TermsModalParagraph>
                                ))}
                                {section?.linkContent && (
                                    <TermsModalParagraph>
                                        {section.linkContent?.start}{" "}
                                        <TermsModalLink href={section.linkContent?.websiteLink?.href}>
                                        {section.linkContent?.websiteLink?.text}
                                        </TermsModalLink>
                                        <TermsModalUnorderList>
                                {section?.linkContent?.points?.map(
                                    (liData: string, index: number) => (
                                        <TermsModalList key={index}>
                                            {liData}
                                        </TermsModalList>
                                    )
                                )}
                            </TermsModalUnorderList>
                                        {section.linkContent?.end}
                                    </TermsModalParagraph>
                                )}
                                
                            </>
                        ))}
                        
                    </PrintableArea>
                    <TermsModalBtns>
                        <WebsiteButton tag="button" onClick={onRequestClose}>
                            Close
                        </WebsiteButton>
                        {/* @ts-ignore */}
                        <PrintButton printableSection={componentRef} />
                    </TermsModalBtns>
                </TermsModalContentContainer>
            </TermsModalContainer>
        </Modal>
    );
};
//@ts-ignore
export default TermsModalPreview;
